import { CardDef, CardInstance, CardAbility } from '../types';
import { getCardById } from '../cards';
import { CategoryColors, CategoryIcons, RarityColors } from './components';

// カード詳細モーダル表示
export function showCardDetail(card: CardDef | CardInstance, onClose?: () => void): void {
  // CardInstanceの場合はCardDefを取得
  const def = 'defId' in card ? getCardById(card.defId) : card;
  if (!def) return;

  const instance = 'defId' in card ? card : null;
  const currentPower = instance?.currentPower ?? def.basePower;
  const powerDiff = instance ? currentPower - def.basePower : 0;

  const catColor = CategoryColors[def.category] ?? '#666';
  const catIcon = CategoryIcons[def.category] ?? '?';
  const rarityColor = RarityColors[def.rarity] ?? '#aaa';

  const overlay = document.createElement('div');
  overlay.id = 'cardDetailModal';
  overlay.innerHTML = `
    <div class="card-detail-overlay" style="
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.85);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 4000;
      animation: fadeIn 0.2s ease-out;
    " id="cardDetailBackdrop">
      <div class="card-detail-content" style="
        background: linear-gradient(145deg, #1e1e30 0%, #151525 100%);
        border: 2px solid ${catColor};
        border-radius: 16px;
        padding: 0;
        width: 320px;
        max-width: 90vw;
        box-shadow: 0 0 40px ${catColor}40;
        animation: slideUp 0.3s ease-out;
        overflow: hidden;
      ">
        <!-- ヘッダー -->
        <div style="
          background: linear-gradient(135deg, ${catColor}30 0%, transparent 100%);
          padding: 20px;
          border-bottom: 1px solid ${catColor}40;
        ">
          <div style="display: flex; justify-content: space-between; align-items: flex-start;">
            <div>
              <div style="font-size: 2rem; margin-bottom: 4px;">${catIcon}</div>
              <h2 style="color: #fff; margin: 0; font-size: 1.4rem;">${def.name}</h2>
              <div style="
                color: ${rarityColor};
                font-size: 0.8rem;
                text-transform: uppercase;
                letter-spacing: 1px;
                margin-top: 4px;
              ">${getRarityLabel(def.rarity)}</div>
            </div>
            <div style="
              background: ${catColor};
              color: #fff;
              width: 48px;
              height: 48px;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 1.5rem;
              font-weight: bold;
              box-shadow: 0 0 20px ${catColor};
            ">
              ${currentPower}
              ${powerDiff !== 0 ? `<span style="font-size: 0.7rem; position: absolute; top: -8px; right: -8px; color: ${powerDiff > 0 ? '#2ecc71' : '#e74c3c'};">${powerDiff > 0 ? '+' : ''}${powerDiff}</span>` : ''}
            </div>
          </div>
        </div>

        <!-- カテゴリー情報 -->
        <div style="padding: 16px 20px; border-bottom: 1px solid #333;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="color: #888; font-size: 0.85rem;">カテゴリー:</span>
            <span style="
              color: ${catColor};
              font-weight: bold;
              display: flex;
              align-items: center;
              gap: 4px;
            ">
              ${catIcon} ${getCategoryLabel(def.category)}
            </span>
          </div>
        </div>

        <!-- 能力一覧 -->
        <div style="padding: 16px 20px;">
          <h3 style="color: #00fff5; font-size: 0.9rem; margin: 0 0 12px 0;">能力</h3>
          ${def.abilities.length > 0 ? `
            <div style="display: flex; flex-direction: column; gap: 10px;">
              ${def.abilities.map((ability) => renderAbilityDetail(ability)).join('')}
            </div>
          ` : `
            <div style="color: #666; font-style: italic;">能力なし</div>
          `}
        </div>

        <!-- フレーバーテキスト (あれば) -->
        ${def.flavorText ? `
          <div style="
            padding: 12px 20px;
            background: rgba(0,0,0,0.3);
            border-top: 1px solid #333;
          ">
            <p style="
              color: #888;
              font-style: italic;
              font-size: 0.85rem;
              margin: 0;
              line-height: 1.5;
            ">"${def.flavorText}"</p>
          </div>
        ` : ''}

        <!-- 閉じるボタン -->
        <div style="padding: 16px 20px; border-top: 1px solid #333;">
          <button id="btnCloseCardDetail" style="
            width: 100%;
            background: transparent;
            border: 1px solid #666;
            color: #aaa;
            padding: 10px;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.2s;
          ">閉じる</button>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);

  // 閉じる処理
  const closeModal = () => {
    overlay.style.opacity = '0';
    overlay.style.transition = 'opacity 0.2s';
    setTimeout(() => {
      overlay.remove();
      onClose?.();
    }, 200);
  };

  // 閉じるボタン
  document.getElementById('btnCloseCardDetail')?.addEventListener('click', closeModal);

  // 背景クリックで閉じる
  document.getElementById('cardDetailBackdrop')?.addEventListener('click', (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  });

  // ESCキーで閉じる
  const handleEsc = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeModal();
      document.removeEventListener('keydown', handleEsc);
    }
  };
  document.addEventListener('keydown', handleEsc);
}

// 能力の詳細表示
function renderAbilityDetail(ability: CardAbility): string {
  const timingLabel = getTimingLabel(ability.timing);
  const timingColor = getTimingColor(ability.timing);
  
  // 条件ラベルを取得(空なら表示しない)
  const conditionLabel = ability.effect.condition ? getConditionLabel(ability.effect.condition) : '';

  return `
    <div style="
      background: rgba(255,255,255,0.03);
      border: 1px solid #333;
      border-radius: 8px;
      padding: 10px 12px;
    ">
      <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px;">
        <span style="
          background: ${timingColor};
          color: #fff;
          font-size: 0.7rem;
          padding: 2px 6px;
          border-radius: 4px;
        ">${timingLabel}</span>
        <span style="color: #fff; font-size: 0.9rem; font-weight: bold;">${ability.effect.description}</span>
      </div>
      ${conditionLabel ? `
        <div style="color: #f39c12; font-size: 0.75rem; margin-top: 4px;">
          条件: ${conditionLabel}
        </div>
      ` : ''}
    </div>
  `;
}

// タイミングラベル
function getTimingLabel(timing: string): string {
  const labels: Record<string, string> = {
    onEnter: '登場時',
    onExit: '退場時',
    onFlag: 'フラッグ獲得時',
    onPlay: 'プレイ時',
    onBench: 'ベンチ時',
    onDeckCheck: 'デッキ確認時',
    onBenchCheck: 'ベンチ確認',
    onFlagGet: 'フラッグ取得時',
    onFlagLost: 'フラッグ喪失時',
    onTrophy: 'トロフィー条件',
    onSelect: '選択時',
    passive: '常時',
  };
  return labels[timing] ?? timing;
}

// タイミング色
function getTimingColor(timing: string): string {
  const colors: Record<string, string> = {
    onEnter: '#3498db',
    onExit: '#e74c3c',
    onFlag: '#f39c12',
    onPlay: '#2ecc71',
    onBench: '#27ae60',
    onDeckCheck: '#9b59b6',
    onBenchCheck: '#1abc9c',
    onFlagGet: '#ffd700',
    onFlagLost: '#e67e22',
    onTrophy: '#f1c40f',
    onSelect: '#00bcd4',
    passive: '#9b59b6',
  };
  return colors[timing] ?? '#666';
}

// 条件ラベル
function getConditionLabel(condition: { type: string; category?: string; minCount?: number }): string {
  switch (condition.type) {
    case 'categoryCount':
      return `ベンチに${getCategoryLabel(condition.category ?? '')}が${condition.minCount ?? 1}体以上`;
    case 'benchCount':
      return `ベンチに${condition.minCount ?? 1}体以上`;
    case 'flagHolder':
      return 'フラッグ保持中';
    default:
      return '';
  }
}

// カテゴリーラベル
function getCategoryLabel(category: string): string {
  const labels: Record<string, string> = {
    warrior: '戦士',
    thief: '盗賊',
    mage: '魔法使い',
    machine: '機械',
    undead: 'アンデッド',
    dragon: 'ドラゴン',
    spirit: '精霊',
    beast: '獣',
    angel: '天使',
    demon: '悪魔',
    golem: 'ゴーレム',
    castle: '城',
    city: 'シティ',
    space: '宇宙',
    movie: '映画',
    ship: '船',
    ghost: '幽霊',
    park: '遊園地',
  };
  return labels[category] ?? category;
}

// レアリティラベル
function getRarityLabel(rarity: string): string {
  const labels: Record<string, string> = {
    common: 'コモン',
    rare: 'レア',
    epic: 'エピック',
    legendary: 'レジェンダリー',
  };
  return labels[rarity] ?? rarity;
}

// アニメーション用CSS追加
const detailStyle = document.createElement('style');
detailStyle.textContent = `
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  #btnCloseCardDetail:hover {
    border-color: #00fff5;
    color: #00fff5;
  }
`;
document.head.appendChild(detailStyle);
